/**
 * CardPickerCalculator.tsx
 *
 * Interactive card-picker that lets the user identify a specific tarot card
 * by entering its number and (for Minor Arcana) selecting a suit.
 *
 * ## Modes
 *
 * ### Force-card mode (default, `readingMode = false`)
 * The user picks a card to pin as the override card for all dates.
 * After confirming, `onConfirm(card)` is called and the parent persists the
 * selection to a cookie.
 *
 * ### Reading mode (`readingMode = true`)
 * The user picks a card to view its reading.  No force-card override is applied.
 * After confirming, the hand-scan ritual plays, then `onConfirm(card)` is called
 * (the parent re-shuffles the deck).
 *
 * ## Navigation flow
 * ```
 * calc view
 *   ├─ MAJ selected → enter number 0–21 → OK → preview view
 *   └─ MIN selected → select suit → enter number 1–14 → OK → preview view
 *
 * preview view
 *   ├─ Confirm → (reading mode) hand-scan ritual → onConfirm(card)
 *   │            (force-card mode) onConfirm(card)
 *   └─ Back → calc view
 *
 * reading view  (reading mode only, after hand-scan)
 *   └─ Close → onClose()
 * ```
 *
 * ## Props
 * @prop onConfirm       - Called with the selected `TarotCard` when the user confirms.
 * @prop onClose         - Called when the user dismisses the picker without confirming.
 * @prop currentCardName - Optional name of the currently active force-card (displayed as hint).
 * @prop readingMode     - When `true`, activates reading-only mode (no force-card override).
 */

'use client';

import React, { useState, useCallback } from 'react';
import { TAROT_CARDS, getDatesForCard, type TarotCard } from '@/data/tarotData';
import { useTheme } from '@/lib/themeContext';
import HandScanRitual from './HandScanRitual';

/**
 * Maps Minor Arcana card numbers (1–14) to their conventional name prefixes.
 * Used to construct card IDs such as `"ace-of-cups"` or `"king-of-swords"`.
 */
const NUMBER_NAMES: Record<number, string> = {
  1: 'ace', 2: 'two', 3: 'three', 4: 'four', 5: 'five',
  6: 'six', 7: 'seven', 8: 'eight', 9: 'nine', 10: 'ten',
  11: 'page', 12: 'knight', 13: 'queen', 14: 'king',
};

/** Available Minor Arcana suits with display labels, emoji, and suit-icon image paths. */
const SUITS = [
  { key: 'wands',     label: 'Wands',     emoji: '🪄' , img: 'assets/images/wands.png' },
  { key: 'cups',      label: 'Cups',      emoji: '🏆', img: 'assets/images/cups.png' },
  { key: 'swords',    label: 'Swords',    emoji: '⚔️', img: 'assets/images/swords.png' },
  { key: 'pentacles', label: 'Pentacles', emoji: '⭐', img: 'assets/images/pentacles.png' },
] as const;

/** Union type of valid suit key strings. */
type SuitKey = typeof SUITS[number]['key'];

/**
 * Calculator mode selection.
 * - `"MAJ"` — Major Arcana (numbers 0–21).
 * - `"MIN"` — Minor Arcana (numbers 1–14, requires suit selection).
 * - `null`  — No mode selected yet.
 */
type Mode = 'MAJ' | 'MIN' | null;

/** Props accepted by `CardPickerCalculator`. */
interface CardPickerCalculatorProps {
  /** Called with the chosen card when the user confirms their selection. */
  onConfirm: (card: TarotCard) => void;
  /** Called when the user dismisses the picker without confirming. */
  onClose: () => void;
  /** Name of the currently active force-card, shown as a hint in the header. */
  currentCardName?: string;
  /**
   * When `true`, activates reading-only mode.
   * The hand-scan ritual plays before `onConfirm` is called, and no force-card
   * override is applied.
   * @default false
   */
  readingMode?: boolean;
}

/**
 * Type guard that checks whether a card reading is a structured object.
 *
 * @param reading - The value from `TarotCard.reading` to inspect.
 * @returns `true` if the reading has `upright`, `themes`, `guidance`, and `affirmation` fields.
 */
function isStructuredReading(reading: unknown): reading is { upright: string; themes: string[]; guidance: string; affirmation: string } {
  return typeof reading === 'object' && reading !== null && 'upright' in reading;
}

export default function CardPickerCalculator({ onConfirm, onClose, currentCardName, readingMode = false }: CardPickerCalculatorProps) {
  const [mode, setMode] = useState<Mode>(null);
  const [input, setInput] = useState('');
  const [suit, setSuit] = useState<SuitKey | null>(null);
  // 'calc' = calculator view, 'preview' = card preview view, 'reading' = reading-mode card view
  const [view, setView] = useState<'calc' | 'preview' | 'reading'>('calc');
  const { deckIndex } = useTheme();
  const [previewCard, setPreviewCard] = useState<TarotCard | null>(null);
  const [imageError, setImageError] = useState(false);
  const [handScanOpen, setHandScanOpen] = useState(false);
  const datesForPreviewCard = previewCard ? getDatesForCard(previewCard) : [];

  // ── Derived validation ────────────────────────────────────────────────────
  const numValue = input === '' ? null : parseInt(input, 10);

  const isInvalid = numValue !== null && (
    mode === 'MAJ' ? (numValue < 0 || numValue > 21) :
    mode === 'MIN' ? (numValue < 1 || numValue > 14) :
    false
  );

  const isOkEnabled =
    mode !== null &&
    input !== '' &&
    !isInvalid &&
    (mode === 'MAJ' || (mode === 'MIN' && suit !== null));

  // ── Live card name lookup ─────────────────────────────────────────────────
  const liveCardName: string | null = (() => {
    if (numValue === null || isInvalid) return null;
    if (mode === 'MAJ') {
      const majorCards = TAROT_CARDS.filter(c => c.arcana === 'major');
      return majorCards[numValue]?.name ?? null;
    }
    if (mode === 'MIN' && suit) {
      const prefix = NUMBER_NAMES[numValue];
      if (!prefix) return null;
      const cardId = `${prefix}-of-${suit}`;
      return TAROT_CARDS.find(c => c.id === cardId)?.name ?? null;
    }
    return null;
  })();

  // ── Button handlers ───────────────────────────────────────────────────────
  const handleDigit = useCallback((d: string) => {
    if (mode === null) return;
    setInput(prev => {
      const next = prev + d;
      if (next.length > 2) return prev;
      return next;
    });
  }, [mode]);

  const handleBackspace = useCallback(() => {
    setInput('');
  }, []);

  const handleMode = useCallback((m: 'MAJ' | 'MIN') => {
    setMode(prev => {
      if (prev === m) return null; // toggle off
      return m;
    });
    setInput('');
    setSuit(null);
  }, []);

  const handleSuit = useCallback((s: SuitKey) => {
    setSuit(prev => prev === s ? null : s);
  }, []);

  const handleOk = useCallback(() => {
    if (!isOkEnabled || numValue === null) return;

    let card: TarotCard | undefined;

    if (mode === 'MAJ') {
      const majorCards = TAROT_CARDS.filter(c => c.arcana === 'major');
      card = majorCards[numValue];
    } else if (mode === 'MIN' && suit) {
      const prefix = NUMBER_NAMES[numValue];
      if (prefix) {
        const cardId = `${prefix}-of-${suit}`;
        card = TAROT_CARDS.find(c => c.id === cardId);
      }
    }

    if (!card) return;

    if (readingMode) {
      setPreviewCard(card);
      setImageError(false);
      setView('reading');
      return;
    }

    onConfirm(card);
    onClose();
  }, [isOkEnabled, numValue, mode, suit, readingMode, onConfirm, onClose]);

  const handleConfirm = useCallback(() => {
    if (previewCard) {
      onConfirm(previewCard);
    }
  }, [previewCard, onConfirm]);

  const handleBackToCalc = useCallback(() => {
    setView('calc');
    setPreviewCard(null);
  }, []);

  const handleHandScanComplete = useCallback(() => {
    setHandScanOpen(false);
    onConfirm(TAROT_CARDS[0]); // signal completion — caller handles shuffle/calc
    onClose();
  }, [onConfirm, onClose]);

  // ── Digit disabled logic ──────────────────────────────────────────────────
  const isDigitDisabled = useCallback((d: string): boolean => {
    if (mode === null) return true;
    // If no digit entered yet, all digits are enabled
    if (input === '') return false;

    const digit = parseInt(d, 10);
    const firstDigit = parseInt(input[0], 10);

    if (mode === 'MAJ') {
      // MAJ valid range: 0–21
      // After pressing 2 as first digit, only 0 and 1 are valid second digits (20, 21)
      // After pressing 0 or 1 as first digit, all second digits are valid (00–19)
      // After pressing 3–9, no second digit is valid (30+ > 21), so disable all
      if (input.length === 1) {
        if (firstDigit === 2) {
          // Only 0 and 1 are valid (20, 21)
          return digit > 1;
        }
        if (firstDigit >= 3) {
          // 30+ is invalid, disable all second digits
          return true;
        }
        // firstDigit is 0 or 1: all second digits valid
        return false;
      }
      return false;
    }

    if (mode === 'MIN') {
      // MIN valid range: 1–14
      // After pressing a digit > 1 as first digit, no second digit is valid (20+ > 14)
      // After pressing 1 as first digit, only 0–4 are valid (10–14)
      if (input.length === 1) {
        if (firstDigit > 1) {
          // e.g. 2x, 3x … all > 14, disable all
          return true;
        }
        if (firstDigit === 1) {
          // Valid: 10–14, so second digit must be 0–4
          return digit > 4;
        }
        // firstDigit === 0: 0x is invalid for MIN anyway (handled by isInvalid), but allow input
        return false;
      }
      return false;
    }

    return false;
  }, [mode, input]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {handScanOpen && (
        <HandScanRitual
          onComplete={handleHandScanComplete}
          onClose={() => setHandScanOpen(false)}
        />
      )}
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.65)' }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerMove={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}>

        <div className="relative bg-card border border-border/60 rounded-2xl shadow-2xl overflow-hidden animate-modal-in"
          style={{ width: view === 'reading' ? 340 : 280, maxWidth: '95vw', maxHeight: '90vh' }}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerMove={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}>

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-2 right-3 z-10 text-muted-foreground hover:text-primary transition-colors text-xl leading-none p-1"
            aria-label="Close card picker">
            ×
          </button>

          {view === 'calc' ? (
            <div className="p-4 pt-5">
              {/* Title */}
              <div className="text-center mb-3">
                {readingMode ? (
                  <span className="font-decorative text-xs text-primary tracking-widest uppercase leading-tight block px-4">
                    Enter a card number to get its reading
                  </span>
                ) : (
                  <>
                    <span className="font-decorative text-xs text-primary tracking-widest uppercase">Card Picker</span>
                    {currentCardName && (
                      <div className="mt-1 text-[11px] text-muted-foreground/70 font-body">
                        <span className="text-primary/80 font-display">{currentCardName}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* MAJ / MIN row */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  onClick={() => handleMode('MAJ')}
                  className={`py-2 rounded-lg text-sm font-display tracking-wider transition-all duration-150 border
                    ${mode === 'MAJ' ?'bg-primary text-background border-primary shadow-[0_0_10px_rgba(201,168,76,0.4)]' :'bg-muted/30 text-foreground border-border/40 hover:border-primary/50'}`}>
                  MAJ
                </button>
                <button
                  onClick={() => handleMode('MIN')}
                  className={`py-2 rounded-lg text-sm font-display tracking-wider transition-all duration-150 border
                    ${mode === 'MIN' ?'bg-primary text-background border-primary shadow-[0_0_10px_rgba(201,168,76,0.4)]' :'bg-muted/30 text-foreground border-border/40 hover:border-primary/50'}`}>
                  MIN
                </button>
              </div>

              {/* Suit buttons — only when MIN is active */}
              {mode === 'MIN' && (
                <div className="grid grid-cols-4 gap-1.5 mb-3 suits-btns">
                  {SUITS.map(s => (
                    <button
                      key={s.key}
                      onClick={() => handleSuit(s.key)}
                      className={`flex flex-col items-center py-1.5 px-1 rounded-lg text-[10px] font-display tracking-wide transition-all duration-150 border
                        ${suit === s.key
                          ? 'bg-primary/20 text-primary border-primary shadow-[0_0_8px_rgba(201,168,76,0.3)]'
                          : 'bg-muted/20 text-muted-foreground border-border/30 hover:border-primary/40'}`}>
                      <span className="text-base leading-none mb-0.5">{/*s.emoji*/}
                        <img 
                          src={s.img}
                          alt={s.key}>
                        </img>
                        </span>
                      <span className="leading-tight">{s.label}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Numeric pad */}
              <div className="grid grid-cols-3 gap-1.5 mb-3">
                {['1','2','3','4','5','6','7','8','9'].map(d => (
                  <button
                    key={d}
                    onClick={() => handleDigit(d)}
                    disabled={isDigitDisabled(d)}
                    className={`py-2.5 rounded-lg text-sm font-display tracking-wider transition-all duration-150 border
                      ${isDigitDisabled(d)
                        ? 'bg-muted/40 text-muted-foreground/50 border-border/30 cursor-not-allowed opacity-50'
                        : 'bg-muted/30 text-foreground border-border/40 hover:bg-primary/10 hover:border-primary/50 active:scale-95'}`}>
                    {d}
                  </button>
                ))}
                {/* Bottom row: ✕ | 0 | OK */}
                <button
                  onClick={handleBackspace}
                  disabled={mode === null}
                  className={`py-2.5 rounded-lg text-xs font-display tracking-wider transition-all duration-150 border
                    ${mode === null
                      ? 'bg-muted/40 text-muted-foreground/50 border-border/30 cursor-not-allowed opacity-50'
                      : 'bg-muted/30 text-red-400 border-red-500/40 hover:bg-red-500/10 hover:border-red-500/60 active:scale-95'}`}>
                  ✕
                </button>
                <button
                  onClick={() => handleDigit('0')}
                  disabled={isDigitDisabled('0')}
                  className={`py-2.5 rounded-lg text-sm font-display tracking-wider transition-all duration-150 border
                    ${isDigitDisabled('0')
                      ? 'bg-muted/40 text-muted-foreground/50 border-border/30 cursor-not-allowed opacity-50' :'bg-muted/30 text-foreground border-border/40 hover:bg-primary/10 hover:border-primary/50 active:scale-95'}`}>
                  0
                </button>
                <button
                  onClick={handleOk}
                  disabled={!isOkEnabled}
                  className={`py-2.5 rounded-lg text-sm font-display tracking-wider transition-all duration-150 border
                    ${isOkEnabled
                      ? 'bg-green-500/20 text-green-400 border-green-500/60 hover:bg-green-500/30 active:scale-95' :'bg-muted/40 text-muted-foreground/50 border-border/30 cursor-not-allowed opacity-50'}`}>
                  OK
                </button>
              </div>

              {/* Display field */}
              <div className={`rounded-lg border px-3 py-2 text-center font-display text-lg tracking-widest min-h-[40px] transition-colors duration-150
                ${isInvalid
                  ? 'border-red-500/60 text-red-400 bg-red-500/5' :'border-border/40 text-foreground bg-muted/20'}`}>
                {input !== '' ? input : <span className="text-muted-foreground/30 text-sm">—</span>}
              </div>

              {/* Hint text */}
              <div className="mt-1.5 text-center min-h-[16px]">
                {mode === 'MAJ' && (
                  liveCardName
                    ? <span className="text-[11px] text-primary/80 font-display tracking-wide"><strong>{liveCardName}</strong></span>
                    : <span className="text-[10px] text-muted-foreground/60 font-body">0 (Fool) – 21 (World)</span>
                )}
                {mode === 'MIN' && (
                  liveCardName
                    ? <span className="text-[11px] text-primary/80 font-display tracking-wide"><strong>{liveCardName}</strong></span>
                    : <span className="text-[10px] text-muted-foreground/60 font-body">1 (Ace) – 14 (King){!suit ? ' · select a suit' : ''}</span>
                )}
              </div>

              {/* OR… / Read my cards — only in reading mode */}
              {readingMode && (
                <div className="mt-4 flex flex-col items-center gap-2">
                  <p className="font-display text-xs tracking-widest text-muted-foreground/60 uppercase">
                    OR…
                  </p>
                  <button
                    onClick={() => setHandScanOpen(true)}
                    className="w-full py-2.5 rounded-xl font-display text-sm tracking-wider uppercase transition-all duration-200 active:scale-95 border"
                    style={{
                      background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 50%, #1e40af 100%)',
                      borderColor: 'rgba(59,130,246,0.5)',
                      color: '#fff',
                      boxShadow: '0 0 16px rgba(37,99,235,0.4)',
                    }}
                  >
                    ✦ Read my cards
                  </button>
                </div>
              )}
            </div>
          ) : view === 'preview' ? (
            /* This has been removed... */
            /* ── Card Preview (normal mode — shows confirm/back) ── */
            <div className="p-4 pt-5">
              <div className="text-center mb-3">
                <span className="font-decorative text-xs text-primary tracking-widest uppercase">Selected Card</span>
              </div>

              {previewCard && (
                <div className="flex flex-col items-center gap-3">
                  {/* Card image */}
                  <div className="w-28 rounded-xl overflow-hidden border border-border/60 shadow-lg">
                    {!imageError && previewCard.imageUrl[deckIndex] ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={previewCard.imageUrl[deckIndex]}
                        alt={`Tarot card: ${previewCard.name}`}
                        className="w-full object-cover"
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <div className="w-full h-40 flex items-center justify-center bg-muted/30 text-muted-foreground text-xs font-display">
                        {previewCard.name}
                      </div>
                    )}
                  </div>

                  {/* Card name */}
                  <p className="font-display text-sm text-primary tracking-wider text-center">
                    {previewCard.name}
                  </p>

                  {/* Confirm / Back buttons */}
                  <div className="flex gap-3 mt-1">
                    <button
                      onClick={handleBackToCalc}
                      className="w-11 h-11 rounded-full flex items-center justify-center border-2 border-red-500/60 text-red-400 hover:bg-red-500/10 transition-all duration-150 text-lg active:scale-95"
                      aria-label="Go back to calculator">
                      ✕
                    </button>
                    <button
                      onClick={handleConfirm}
                      className="w-11 h-11 rounded-full flex items-center justify-center border-2 border-green-500/60 text-green-400 hover:bg-green-500/10 transition-all duration-150 text-lg active:scale-95"
                      aria-label="Confirm card selection">
                      ✓
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* ── Reading Mode Card View ── */
            <div className="overflow-y-auto scrollbar-arcane" style={{ maxHeight: '85vh' }}>
              {previewCard && (
                <div className="p-5">
                  {/* Back button */}
                  <button
                    onClick={handleBackToCalc}
                    className="mb-3 flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors text-xs font-display tracking-wider"
                    aria-label="Back to calculator">
                    ← Back
                  </button>

                  {/* Card identity */}
                  <div className="flex flex-col items-center gap-1 mb-4 text-center">
                    <div className="flex items-center gap-2">
                      <span className="text-primary/40 text-sm" aria-hidden>✦</span>
                      <h2 className="font-decorative text-lg text-primary tracking-wide">
                        {previewCard.name}
                      </h2>
                      <span className="text-primary/40 text-sm" aria-hidden>✦</span>
                    </div>
                    <p className="font-body text-xs text-muted-foreground italic">
                      {previewCard.arcana === 'major' ? 'Major Arcana' : `${previewCard.suit} — Minor Arcana`}
                    </p>
                  </div>

                  <div className="arcane-divider mb-4" />

                  {/* Keywords */}
                  <div className="flex flex-wrap justify-center gap-1.5 mb-4">
                    {previewCard.keywords.map(kw => (
                      <span
                        key={`reading-kw-${previewCard.id}-${kw}`}
                        className="font-body text-[10px] text-primary/70 italic px-2 py-0.5 rounded-full border border-primary/20 bg-primary/5"
                      >
                        {kw}
                      </span>
                    ))}
                  </div>

                  {/* Card image */}
                  <div className="flex justify-center mb-4">
                    <div className="w-32 rounded-xl overflow-hidden border border-border/60 shadow-lg">
                      {!imageError && previewCard.imageUrl[deckIndex] ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={previewCard.imageUrl[deckIndex]}
                          alt={`Tarot card: ${previewCard.name}`}
                          className="w-full object-cover"
                          onError={() => setImageError(true)}
                        />
                      ) : (
                        <div className="w-full h-44 flex items-center justify-center bg-muted/30 text-muted-foreground text-xs font-display">
                          {previewCard.name}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Reading */}
                  <div className="mb-4">
                    <h3 className="font-display text-xs text-muted-foreground/60 tracking-widest uppercase mb-2 text-center">
                      ✦ The Reading ✦
                    </h3>
                    {isStructuredReading(previewCard.reading) ? (
                      <p className="font-body text-sm text-foreground/85 leading-relaxed italic text-center">
                        {previewCard.reading.upright[deckIndex]}
                      </p>
                    ) : (
                      <div
                        className="font-body text-sm text-foreground/85 leading-relaxed italic text-center"
                        dangerouslySetInnerHTML={{ __html: previewCard.reading as unknown as string }}
                      />
                    )}
                  </div>

                  {/* Themes */}
                  {isStructuredReading(previewCard.reading) && (
                    <div className="mb-4 bg-muted/30 rounded-xl p-3 border border-border/50">
                      <h3 className="font-display text-[10px] text-muted-foreground/50 tracking-widest uppercase mb-2 text-center">
                        Themes
                      </h3>
                      <div className="grid grid-cols-2 gap-1.5">
                        {previewCard.reading.themes.map((theme, i) => (
                          <div key={`reading-theme-${previewCard.id}-${i}`} className="flex items-start gap-1.5">
                            <span className="text-primary/60 text-xs mt-0.5 shrink-0" aria-hidden>◆</span>
                            <span className="font-body text-xs text-foreground/70 leading-snug">{theme}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Guidance */}
                  {isStructuredReading(previewCard.reading) && (
                    <div className="mb-4">
                      <h3 className="font-display text-[10px] text-muted-foreground/50 tracking-widest uppercase mb-2 text-center">
                        ✦ Guidance ✦
                      </h3>
                      <p className="font-body text-sm text-foreground/75 leading-relaxed text-center">
                        {previewCard.reading.guidance}
                      </p>
                    </div>
                  )}

                  {/* Affirmation */}
                  {isStructuredReading(previewCard.reading) && (
                    <div className="bg-gradient-to-br from-primary/8 to-accent/8 rounded-xl p-3 border border-primary/20">
                      <h3 className="font-display text-[10px] text-primary/60 tracking-widest uppercase mb-2 text-center">
                        Affirmation
                      </h3>
                      <p className="font-body text-sm text-primary/90 leading-relaxed italic text-center">
                        &ldquo;{previewCard.reading.affirmation}&rdquo;
                      </p>
                    </div>
                  )}

                              {/* Days with this card */}
                  <div className="mb-5">
                    <h3 className="font-display text-[10px] text-muted-foreground/50 tracking-widest uppercase mb-2 text-center">
                      The {previewCard.name} is on:
                    </h3>
                    <p className="font-body text-sm text-foreground/75 leading-relaxed text-center">
                      {datesForPreviewCard.join(', ')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
